import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { logOperationalError, logOperationalWarning } from '../../utils/safeLogger';
import styles from './ZodiacClashStyles';
import ZodiacClashGlobalStyles from './ZodiacClashGlobalStyles';
import ClashRewardModal from './components/ClashRewardModal';
import ClashLoadingState from './components/ClashLoadingState';
import ClashErrorState from './components/ClashErrorState';
import ClashEmptyState from './components/ClashEmptyState';
import ClashDominion from './components/ClashDominion';
import ClashCards from './components/ClashCards';
import ClashMetaStrip from './components/ClashMetaStrip';
import ClashResultNarrative from './components/ClashResultNarrative';
import ClashToast from './components/ClashToast';
import ClashRoundHeader from './components/ClashRoundHeader';
import {
  getZodiacClashToday,
  voteZodiacClash,
  getZodiacClashDominion,
} from '../../services/zodiacClashApi';


import {
  MAX_VOTES,
  ELEMENT_VISUAL,
  getElementVisual,
  buildClashContext,
  formatCountdownToNextReset,
  getSignEmoji,
  getClashStreakState,
  setClashStreakState,
  getPlayerProfileState,
  registerVoteInProfile,
  buildResultNarrative,
  pickClashQuestionPremium,
  pickInsightPremium,
  mapTodayToDay,
} from './ZodiacClashHelpers';
import { localizedServerContent } from '../../localization/presentationLanguage';


export default function ZodiacClashScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('zodiacClash');
  const isMobile = useMemo(() => {
    try {
      return window?.matchMedia ? window.matchMedia('(max-width: 520px)').matches : (window.innerWidth <= 520);
    } catch {
      return false;
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(null);
  const [error, setError] = useState(null);
  const [voting, setVoting] = useState(false);
  const [justVoted, setJustVoted] = useState(null);
  // toast premium: feedback inmediato sin “ensuciar” el siguiente duelo
  const [toast, setToast] = useState(null); // { text: string, tone: 'gold'|'violet' }

  // Reveal de resultados: no mostramos % antes de que el usuario elija
  // (evita sesgo por “efecto manada”).
  const [revealResults, setRevealResults] = useState(false);
  const [revealedPct, setRevealedPct] = useState(null); // { pctA, pctB }
  const [animatedPct, setAnimatedPct] = useState({ pctA: 0, pctB: 0 });
  const [completedCountdown, setCompletedCountdown] = useState(formatCountdownToNextReset());

const [streak, setStreak] = useState(() => getClashStreakState());
const predictionResult = null;
const [resultNarrative, setResultNarrative] = useState('');
// eslint-disable-next-line no-unused-vars
const [profileState, setProfileState] = useState(() => getPlayerProfileState());

  // Clash reward modal (3/3 daily completion)
  const [clashRewardModal, setClashRewardModal] = useState(null); // { insight, xpGranted, boosterGranted, newLevel, newXp }

  // Toast/voting/advance timers and voting guard refs
  const toastTimerRef = useRef(null);
  const votingRef = useRef(false);
  const advanceTimerRef = useRef(null);
  const revealAnimRef = useRef(0);

  // =========================
  // Dominion (ranking de signos)
  // - Lazy-load: se pide al abrir (no penaliza el render del duelo)
  // =========================
  const [dominionOpen, setDominionOpen] = useState(false);
  const [dominionRange, setDominionRange] = useState('7d');
  const [dominionLoading, setDominionLoading] = useState(false);
  const [dominionError, setDominionError] = useState(null);
  const [dominion, setDominion] = useState(null); // { items,totalVotes,startDayKey,endDayKey,range }
  const [dominionAnimate, setDominionAnimate] = useState(false);

  const loadDominion = useCallback(async (range = dominionRange) => {
    try {
      setDominionError(null);
      setDominionAnimate(false);
      setDominionLoading(true);

      const data = await getZodiacClashDominion({ range });
      if (!data || data.ok !== true) throw new Error(t('dominionLoadError'));

      setDominion(data);

      // Animación suave de barras (evita que parezca “tabla estática”)
      // Se dispara solo cuando ya hay datos.
      try {
        requestAnimationFrame(() => setDominionAnimate(true));
      } catch {
        setTimeout(() => setDominionAnimate(true), 0);
      }
    } catch (e) {
      setDominion(null);
      setDominionError(localizedServerContent(e?.payload?.error || e?.message, t('dominionLoadError')));
    } finally {
      setDominionLoading(false);
    }
  }, [dominionRange, t]);

  const toggleDominion = useCallback(() => {
    setDominionOpen((v) => {
      const next = !v;
      if (next) {
        loadDominion(dominionRange);
      } else {
        // al cerrar, reseteamos animación para que al abrir se vea premium
        setDominionAnimate(false);
      }
      return next;
    });
  }, [loadDominion, dominionRange]);

  const handleChangeDominionRange = useCallback((range) => {
    setDominionAnimate(false);
    setDominionRange(range);
    if (dominionOpen) loadDominion(range);
  }, [dominionOpen, loadDominion]);


  const loadDay = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const data = await getZodiacClashToday();
      // Use the new mapper to resolve day data
      const resolvedDay = mapTodayToDay(data);

      if (!resolvedDay) {
        logOperationalWarning('zodiac-clash.load', { code: 'unexpected_response' });
        setDay(null);
        setError(t('unexpectedNoDuel'));
        return;
      }

      const backendCurrentStreak = Number(resolvedDay?.currentStreak || 0);
      const backendBestStreak = Number(resolvedDay?.bestStreak || 0);
      setStreak((prev) => {
        const next = {
          current: backendCurrentStreak,
          best: backendBestStreak,
          lastCompletedDayKey:
            backendCurrentStreak > 0
              ? (resolvedDay?.dayKey || prev?.lastCompletedDayKey || null)
              : (prev?.lastCompletedDayKey || null),
        };
        setClashStreakState(next);
        return next;
      });

      // If no duel (e.g. already played all 3), still show state
      if (!resolvedDay.signA || !resolvedDay.signB) {
        setDay(resolvedDay);
        return;
      }

      setDay(resolvedDay);
    } catch (e) {
      logOperationalError('zodiac-clash.load', { status: e?.status, code: 'request_failed' });
      setDay(null);
      setError(localizedServerContent(e?.payload?.message || e?.message, t('todayLoadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);


  useEffect(() => {
    loadDay();
  }, [loadDay]);

  // Si cambia el duelo, limpiamos el estado de micro-feedback (evita repeticiones visuales)
useEffect(() => {
    setJustVoted(null);
    setRevealResults(false);
    setRevealedPct(null);

   

    // reset micro-feedback timers/state when the duel changes
    try { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); } catch {}
    toastTimerRef.current = null;
    try { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); } catch {}
    advanceTimerRef.current = null;
    votingRef.current = false;
    setToast(null);
  }, [day?.clashId, day?._id, day?.dayKey]);

  // Cleanup effect to clear timers on unmount
  useEffect(() => {
    return () => {
      try { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); } catch {}
      toastTimerRef.current = null;
      try { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); } catch {}
      advanceTimerRef.current = null;
      votingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!revealResults) {
      setAnimatedPct({ pctA: 0, pctB: 0 });
      if (revealAnimRef.current) cancelAnimationFrame(revealAnimRef.current);
      revealAnimRef.current = 0;
      return;
    }

    const targetA = Math.max(0, Math.min(100, Number(revealedPct?.pctA ?? 0)));
    const targetB = Math.max(0, Math.min(100, Number(revealedPct?.pctB ?? 0)));
    const duration = 620;
    const start = performance.now();

    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const e = easeOut(p);
      setAnimatedPct({
        pctA: Math.round(targetA * e),
        pctB: Math.round(targetB * e),
      });
      if (p < 1) {
        revealAnimRef.current = requestAnimationFrame(tick);
      } else {
        revealAnimRef.current = 0;
      }
    };

    if (revealAnimRef.current) cancelAnimationFrame(revealAnimRef.current);
    revealAnimRef.current = requestAnimationFrame(tick);

    return () => {
      if (revealAnimRef.current) cancelAnimationFrame(revealAnimRef.current);
      revealAnimRef.current = 0;
    };
  }, [revealResults, revealedPct]);

  const votesLeft = MAX_VOTES - (day?.myVotes || 0);
  const backendProfile = day?.profile || null;
  const profileTitle = localizedServerContent(backendProfile?.title, t('formingProfile'));
  const profileSubtitle = (() => {
    const base = localizedServerContent(backendProfile?.subtitle, t('playMore'));
    const topSigns = Array.isArray(backendProfile?.topSigns) ? backendProfile.topSigns : [];
    if (!topSigns.length) return base;
    const names = topSigns.map((it) => it?.label).filter(Boolean).join(` ${t('and')} `);
    return names ? `${base} ${t('affinity')}: ${names}.` : base;
  })();
  const locked = votesLeft <= 0;

  const duelNumber = Math.min((Number(day?.myVotes) || 0) + 1, MAX_VOTES);
  const isDecisiveDuel = duelNumber === MAX_VOTES;

  useEffect(() => {
    if (!locked) return;
    const id = setInterval(() => {
      setCompletedCountdown(formatCountdownToNextReset());
    }, 30000);
    setCompletedCountdown(formatCountdownToNextReset());
    return () => clearInterval(id);
  }, [locked]);


  // Pregunta del duelo (premium): siempre calculada con Hooks en orden fijo.
  // Es determinística por day+clash, y filtra preguntas sin sentido.
  const displayQuestion = useMemo(() => {
    const question = pickClashQuestionPremium({
      rawQuestion: day?.question,
      dayKey: day?.dayKey || day?._id,
      clashId: day?.clashId,
      signA: day?.signA,
      signB: day?.signB,
    });

    const cleanedQuestion = String(question || '')
      .replace(/\s+/g, ' ')
      .replace(/\s*[⚡💭🔒👑📊⚔️🎲💬🔥🧭✨]\s*\?*$/u, '')
      .replace(/\?+$/u, '')
      .trim();

    return cleanedQuestion ? `${cleanedQuestion}?` : '';
  }, [
    day?.question,
    day?.dayKey,
    day?._id,
    day?.clashId,
    day?.signA,
    day?.signB,
  ]);

  const handleVote = useCallback(async (choice) => {
    // Synchronous guard against double clicks before state updates propagate
    if (votingRef.current) return;

    if (!day || voting || revealResults) return;
    if (day.myVotes >= MAX_VOTES) return;

    votingRef.current = true;
    setJustVoted(choice);

    const chosenSign = choice === 'A' ? day.signA : day.signB;
    const opponentSign = choice === 'A' ? day.signB : day.signA;

    // Feedback premium (determinístico por duelo y sin repetición)
    const t = pickInsightPremium({
      sign: chosenSign,
      opponent: opponentSign,
      question: displayQuestion || day.question,
      dayKey: day.dayKey || day._id,
      clashId: day.clashId,
    });

    if (t) {
      // evitar que se acumulen timeouts si el usuario vota rápido
      try { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); } catch {}
      toastTimerRef.current = null;

      setToast(null);
      const emoji = getSignEmoji(chosenSign);
      setToast({
        text: t,
        tone: choice === 'A' ? 'gold' : 'violet',
        emoji,
      });

      // auto-dismiss (un poco más de tiempo para leer)
      try {
        toastTimerRef.current = setTimeout(() => {
          toastTimerRef.current = null;
          setToast(null);
        }, 2800);
      } catch {
        // fallback best-effort
        setTimeout(() => setToast(null), 2800);
      }
    }

    try {
      setVoting(true);

      // 1) Registrar el voto
      const voteRes = await voteZodiacClash({
        dayKey: day.dayKey || day._id,
        clashId: day.clashId,
        picked: choice,
      });

      // 2) Revelar resultados SOLO después de elegir
      // (si el backend aún no trae pct, hacemos fallback calculando con votos)
      const pctAFromApi = Number(voteRes?.pctA);
      const pctBFromApi = Number(voteRes?.pctB);

      const hasPctFromApi = Number.isFinite(pctAFromApi) && Number.isFinite(pctBFromApi);

      let pctA = hasPctFromApi ? pctAFromApi : 50;
      let pctB = hasPctFromApi ? pctBFromApi : 50;

      if (!hasPctFromApi) {
        const vA = Number(voteRes?.votesA ?? 0);
        const vB = Number(voteRes?.votesB ?? 0);
        const tot = vA + vB;
        if (tot > 0) {
          pctA = Math.round((vA / tot) * 100);
          pctB = 100 - pctA;
        }
      }

      setRevealedPct({ pctA, pctB });
      setRevealResults(true);

      const resolvedWinnerChoice = pctA > pctB ? 'A' : pctB > pctA ? 'B' : null;

const baseNarrative = buildResultNarrative({
  signA: day.signA,
  signB: day.signB,
  pctA,
  pctB,
  winnerChoice: resolvedWinnerChoice,
  question: displayQuestion || day.question,
});



const decisiveReading =
  Number(day?.myVotes || 0) + 1 >= MAX_VOTES
    ? ' Clash completado: tus tres decisiones de hoy ya forman parte del Dominio Zodiacal.'
    : '';

const cleanedBaseNarrative = String(baseNarrative || '')
  .replace(/\s*Coincidiste con el \d+% de la comunidad al elegir [^.]+\.?/gi, '')
  .replace(/\s*Elegiste [^,]+, una lectura compartida por solo el \d+% de la comunidad\.?/gi, '')
  .replace(/\s*Tu elección quedó empatada con el \d+% de la comunidad\.?/gi, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

setResultNarrative(
  `${cleanedBaseNarrative}${decisiveReading}`.trim()
);


const nextProfile = registerVoteInProfile(choice === 'A' ? day.signA : day.signB, {
  predictionChoice: null,
  winnerChoice: resolvedWinnerChoice,
});
setProfileState(nextProfile);

      // 3) Check for 3/3 completion reward
      if (voteRes?.clashReward?.claimed === true) {
        setClashRewardModal(voteRes.clashReward);
      }

      const backendCurrentStreak = Number(voteRes?.currentStreak ?? 0);
      const backendBestStreak = Number(voteRes?.bestStreak ?? 0);
      setStreak((prev) => {
        const next = {
          current: backendCurrentStreak,
          best: backendBestStreak,
          lastCompletedDayKey:
            backendCurrentStreak > 0
              ? (voteRes?.dayKey || day?.dayKey || day?._id || prev?.lastCompletedDayKey || null)
              : (prev?.lastCompletedDayKey || null),
        };
        setClashStreakState(next);
        return next;
      });

    } catch (e) {
      try { alert(e?.message || 'No se pudo registrar el voto'); } catch {}
    } finally {
      setVoting(false);
      votingRef.current = false;
    }
  }, [day, voting, revealResults, displayQuestion]);

  if (loading) {
    return <ClashLoadingState styles={styles} />;
  }

  if (error) {
    return <ClashErrorState styles={styles} error={error} onRetry={loadDay} />;
  }

  if (!day) {
    return (
      <ClashErrorState
        styles={styles}
        message={t('noDuelData')}
        onRetry={loadDay}
      />
    );
  }


  const hasClash = !!(day.signA && day.signB && day.clashId);

  const clashContext = hasClash ? buildClashContext(day.signA, day.signB) : '';
  const visualA = hasClash ? getElementVisual(day.signA) : ELEMENT_VISUAL.cosmic;
  const visualB = hasClash ? getElementVisual(day.signB) : ELEMENT_VISUAL.cosmic;


  // %: Por UX, NO mostramos porcentajes antes de elegir (evita predisposición).
  // Recién se revelan cuando el usuario vota este duelo.
  const showPercents = !!revealResults;

  let percentA = showPercents ? 50 : null;
  let percentB = showPercents ? 50 : null;

  if (showPercents) {
    const pA = Number(revealedPct?.pctA ?? day?.pctA);
    const pB = Number(revealedPct?.pctB ?? day?.pctB);

    if (Number.isFinite(pA) && Number.isFinite(pB)) {
      percentA = pA;
      percentB = pB;
    } else {
      // fallback suave
      const vA = Number(day?.votesA ?? 0);
      const vB = Number(day?.votesB ?? 0);
      const tot = vA + vB;
      if (tot > 0) {
        percentA = Math.round((vA / tot) * 100);
        percentB = 100 - percentA;
      }
    }

    // si no hay votos reales y el backend devuelve 0/0, mostramos 50/50
    const totalRealVotes = Number(day?.votesA ?? 0) + Number(day?.votesB ?? 0);
    if (totalRealVotes <= 0 && percentA === 0 && percentB === 0) {
      percentA = 50;
      percentB = 50;
    }
  }

  const displayPctA = showPercents ? animatedPct.pctA : null;
  const displayPctB = showPercents ? animatedPct.pctB : null;
  const winnerChoice = showPercents
    ? (percentA > percentB ? 'A' : percentB > percentA ? 'B' : null)
    : null;

  return (
    <div style={styles.page}>
      <ClashRewardModal
        reward={clashRewardModal}
        onClose={() => setClashRewardModal(null)}
      />

      <ZodiacClashGlobalStyles />
      <div style={styles.starLayer} aria-hidden />
      <div style={styles.cosmicGlow} aria-hidden />
      <div style={styles.auroraLayer} aria-hidden />
      <Helmet>
        <title>Zodiac Clash ⚔️ | Zodiacus</title>
        <meta
          name="description"
          content={t('metaDescription')}
        />
      </Helmet>

      <nav style={{
        ...styles.navbar,
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 12px)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 12px)',
      }}>
        <button
          type="button"
          onClick={() => navigate('/home')}
          style={styles.navButton}
          aria-label={t('backHome')}
          title={t('backHome')}
        >
          ← {t('home')}
        </button>
        {/* <div style={styles.navTitle}>Zodiac Clash</div> */}
        <div style={{ width: 48, height: 48 }} />
      </nav>

      <header style={styles.header}>
        <h1 className="zdxTitle" style={styles.title}>Zodiac Clash</h1>
        <p style={styles.tagline}>{t('tagline')}</p>
        <div style={styles.votesInfo}>
          <div style={{ fontWeight: 900, letterSpacing: 1.1, textTransform: 'uppercase' }}>
            {t('dailyClash')}
          </div>
          <div style={styles.ritualOrbs}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  ...styles.orb,
                  opacity: i < votesLeft ? 1 : 0.25,
                }}
              />
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1px 1fr',
              alignItems: 'center',
              gap: 16,
              color: '#FFE9A6',
            }}
          >
            <div style={styles.votesCount}>{votesLeft}/{MAX_VOTES} disponibles</div>
            <div style={{ height: 18, background: 'rgba(218,165,32,0.34)' }} />
            <div style={styles.votesCount}>
              {MAX_VOTES - votesLeft}/{MAX_VOTES} ⚔️ completados
            </div>
          </div>
        </div>
      </header>

      {!hasClash && (
        <ClashEmptyState
          styles={styles}
          locked={locked}
          completedCountdown={completedCountdown}
          day={day}
        />
      )}

      {hasClash && (
        <section style={styles.clashSection} aria-label="Duelo zodiacal">
          <ClashRoundHeader
            styles={styles}
            duelNumber={duelNumber}
            maxDuels={MAX_VOTES}
            isDecisive={isDecisiveDuel}
          />

          {displayQuestion && (
            <div className="zdxQuestion" style={styles.question}>
              {displayQuestion}
            </div>
          )}
          {clashContext && (
            <div style={styles.clashContext}>
              {clashContext}
            </div>
          )}

          <ClashCards
            styles={styles}
            isMobile={isMobile}
            visualA={visualA}
            visualB={visualB}
            justVoted={justVoted}
            locked={locked}
            voting={voting}
            revealResults={revealResults}
            winnerChoice={winnerChoice}
            handleVote={handleVote}
            day={day}
            showPercents={showPercents}
            displayPctA={displayPctA}
            displayPctB={displayPctB}
          />

          <ClashMetaStrip
            styles={styles}
            streak={streak}
            backendProfile={backendProfile}
            profileTitle={profileTitle}
            profileSubtitle={profileSubtitle}
          />

          <ClashResultNarrative
            styles={styles}
            revealResults={revealResults}
            resultNarrative={resultNarrative}
            predictionResult={predictionResult}
            day={day}
            winnerChoice={winnerChoice}
            displayPctA={displayPctA}
            displayPctB={displayPctB}
            justVoted={justVoted}
          />

          {revealResults && (
            <button
              type="button"
              onClick={loadDay}
              style={styles.nextDuelButton}
              aria-label={
                Number(day?.myVotes || 0) + 1 >= MAX_VOTES
                  ? t('viewResult')
                  : t('goToNextDuel')
              }
            >
              {Number(day?.myVotes || 0) + 1 >= MAX_VOTES
                ? t('viewResult')
                : t('nextDuel')}
            </button>
          )}


          <ClashToast
            styles={styles}
            toast={toast}
          />


          <ClashDominion
            styles={styles}
            isMobile={isMobile}
            dominionOpen={dominionOpen}
            toggleDominion={toggleDominion}
            dominion={dominion}
            dominionRange={dominionRange}
            handleChangeDominionRange={handleChangeDominionRange}
            loadDominion={loadDominion}
            dominionLoading={dominionLoading}
            dominionError={dominionError}
            dominionAnimate={dominionAnimate}
          />
        </section>
      )}

      {locked && hasClash && (
        <footer style={styles.lockedFooter} role="alert" aria-live="assertive">
          {t('votesUsed')}
        </footer>
      )}

    </div>
  );
}
